import { useCallback, useEffect, useRef, useState } from "react";
import ActivityService from "../../../../services/ActivityService";
import TaskService from "../../../../services/TaskSerivce";
import FileService from "../../../../services/FileService";
import CompanyService from "../../../../services/CompanyService";
import FloatingPanelService from "../../../shared/FloatingPanel";
import { TranslationService } from "../../../../services/TranslationService";
import { ToastService } from "../../../shared/bootstrap/Toast";
import { ActivityPendingResponse } from "../entities/AcitivityPendingResponse";
import { ActivityOrigin } from "../entities/AcitivityListResponse";
import { TaskStatus } from "../../../task/entities/TaskStatus";
import { ActivityItemShell } from "./shell/ActivityItemShell";
import { ActivityShellAction } from "./shell/ActivityShellTypes";
import { AIAgentStrip } from "../components/ActivityEmailDisplay";
import {
  deleteTaskAction,
  deleteScheduledTaskAction
} from "../ActivityHistory";
import TaskDetail from "../../../task/components/TaskDetail";
import TaskStar from "../../../task/components/TaskStar";
import TooltipComponent from "../../../shared/TooltipComponent";
import { LabelActionText } from "../components/NewComment";
import ScheduledTaskTableProvider from "../../../task/components/scheduled/ScheduledTaskTableProvider";
import NewScheduledTaskForm from "../../../task/components/scheduled/add/NewScheduledTaskForm";
import { SafeHtml } from "../../../../components/SafeHtml";
import { EmailCompose } from "../../components/EmailCompose";
import { deleteActivityAction } from "../ActivityHistory";
import { addIf } from "../../../../utils/Utils";
import Loading from "../../../shared/components/Loading";
import ErrorMessage from "../../../shared/components/Error";
import { Client } from "../../entities/ClientGetResponse";
import { formatDateDigits } from "../../../../utils/FormatUtils";
import iconTask from "../../../../assets/img/taskIcon.svg";
import iconTaskPending from "../../../../assets/img/icon-task-pending.svg";
import iconTaskDue from "../../../../assets/img/icon-task-due.svg";
import iconEmail from "../../../../assets/img/iconEmail.svg";

const TagIcon = ({ tags }: { tags?: string | null }) => {
  const { translate } = TranslationService;
  if (!tags || tags.length === 0) return null;
  return (
    <div className="d-flex align-items-center">
      <TooltipComponent title={translate.Tags}>
        <i className="fa fa-tag activity-icon-color my-2" />
      </TooltipComponent>
      <div className="d-flex align-items-center gap-2">
        {tags.split(",").map((tag, i) => (
          <div key={i}>
            <LabelActionText bgColor={false} text={tag} action={() => {}} />
          </div>
        ))}
      </div>
    </div>
  );
};

const TaskFileList = ({
  files,
  taskId
}: {
  files: { fileName: string; id: string }[];
  taskId: number;
}) => {
  const { translate } = TranslationService;
  const [showFiles, setShowFiles] = useState(false);
  const nonInlineFiles = files.filter((f) => !(f as any).inline);
  if (nonInlineFiles.length === 0) return null;

  return (
    <>
      <TooltipComponent title={translate.AttachedFile}>
        <i
          className="far fa-fw fa-paperclip activity-icon-color my-2"
          onClick={(e) => {
            e.stopPropagation();
            setShowFiles(!showFiles);
          }}
        />
      </TooltipComponent>
      {showFiles && (
        <>
          <div
            className="modal-backdrop fade show attachments-modal-backdrop"
            onClick={() => setShowFiles(false)}
          />
          <div
            className="modal fade show attachments-modal"
            tabIndex={-1}
            style={{
              display: "block",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{
                margin: "auto",
                height: "100%",
                display: "flex",
                alignItems: "center"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{translate.Attachments}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowFiles(false)}
                  />
                </div>
                <div className="modal-body">
                  {nonInlineFiles.map((f) => (
                    <div className="col" key={f.id}>
                      <button
                        type="button"
                        className="btn btn-link p-0 m-0 ms-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          FileService.downloadTask(
                            f.id,
                            taskId.toString(),
                            f.fileName
                          );
                        }}
                      >
                        {f.fileName}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowFiles(false)}
                  >
                    {translate.Close}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const PendingTaskItem = ({
  data,
  reload
}: {
  data: ActivityPendingResponse.Task;
  reload: (action?: (val?: unknown) => void) => void;
}) => {
  const [loadingTaskCheck, setLoadingTaskCheck] = useState(false);
  const { translate } = TranslationService;

  const onTaskClick = async () => {
    setLoadingTaskCheck(true);
    await TaskService.toggle(data.TaskID, TaskStatus.Fulfilled);
    await reload(setLoadingTaskCheck);
  };

  const openEditModal = () =>
    FloatingPanelService.showPanel({
      children: (
        <TaskDetail
          taskId={data.TaskID}
          clientId={data.PersonID}
          creatorUserId={data.CreatorUserID}
          dateCreated={data.Created}
          reload={reload}
          isAutonomous={data.Origin === ActivityOrigin.Autonomous}
        />
      ),
      title: "",
      height: 800,
      width: 750
    });

  const hasTaskCheckCondition =
    (CompanyService.canDo("edittask") || CompanyService.canDo("checktask")) &&
    (CompanyService.canDo("edittask") ||
      CompanyService.canDo("checkotherstask") ||
      data.ResponsibleUserID?.toString() === CompanyService.getUserid());

  const creator =
    CompanyService.getUsers()?.find(
      (x) => x.Id === data.ResponsibleUserID?.toString()
    )?.Value ?? translate.NotAssigned;

  const actions: ActivityShellAction[] = [
    ...addIf(CompanyService.canDo("edittask"), {
      icon: "fa-pen text-secondary",
      onClick: openEditModal,
      tooltip: translate.Edit
    }),
    ...addIf(
      CompanyService.canDo("deletetask"),
      deleteTaskAction(
        data.TaskID,
        () => reload(),
        () => ToastService.showToast(translate.ErrorProcessingRequest)
      )
    )
  ];

  const imgSrc = data.dueDays > 0 ? iconTaskDue : iconTaskPending;

  const iconNode = loadingTaskCheck ? (
    <i className="fas fa-spinner-third fa-spin third" />
  ) : (
    <img
      onClick={hasTaskCheckCondition ? onTaskClick : undefined}
      className={`img-fluid${hasTaskCheckCondition ? " pointer" : ""}`}
      src={imgSrc}
      alt="icon task pending"
    />
  );

  const headerNode = (
    <div className="type">
      {translate.Task}
      <span className="creator">
        {" "}
        {translate.To.toLowerCase()} {creator}
      </span>
    </div>
  );

  const contentNode = (
    <>
      <div style={{ whiteSpace: "pre-line" }}>{data.Description}</div>
      <div
        className="d-flex align-items-center justify-center gap-1"
        style={{ color: "#777676" }}
      >
        <TaskFileList files={data.files ?? []} taskId={data.TaskID} />
        <TagIcon tags={data.tags || data.Tags} />
      </div>
    </>
  );

  const dateNode = (
    <div className="dateTertiaryQ d-flex align-items-center gap-2">
      <TaskStar
        Fav={data.Fav}
        TaskID={data.TaskID}
        className="me-1 align-text-top"
      />
      <span className="fw-bold">
        {formatDateDigits(new Date(data.dueDate))}
      </span>
    </div>
  );

  const aiStripNode =
    data.Origin === ActivityOrigin.Autonomous ? (
      <AIAgentStrip text={translate.TaskCreatedByAIAgent} />
    ) : undefined;

  const bgClass = data.dueDays > 0 ? "bgDueTask" : "bgPendingTask";

  return (
    <ActivityItemShell
      cardClassName={`historyCard ${bgClass}`}
      iconNode={iconNode}
      headerNode={headerNode}
      dateNode={dateNode}
      content={contentNode}
      actions={
        CompanyService.canDoAny(["edittask", "deletetask"]) ? actions : []
      }
      aiStripNode={aiStripNode}
      respectViewContext={false}
    />
  );
};

const ScheduledEmailItem = ({
  data,
  reload
}: {
  data: ActivityPendingResponse.Mail;
  reload: () => void;
}) => {
  const { translate } = TranslationService;

  const openEmailModal = useCallback(async () => {
    const emailRequest = await ActivityService.get(data.MessageID);
    if (emailRequest instanceof Error) {
      ToastService.showToast(
        translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      return;
    }
    FloatingPanelService.showPanel({
      title: translate.EmailToClient,
      children: (
        <EmailCompose
          personId={data.PersonID.toString()}
          emailBase={emailRequest.item}
          onSubmit={reload}
        />
      ),
      width: 900,
      height: 669
    });
  }, [
    data.MessageID,
    data.PersonID,
    reload,
    translate.EmailToClient,
    translate.ErrorProcessingRequest
  ]);

  const actions: ActivityShellAction[] = [
    ...addIf(CompanyService.canDo("editmail"), {
      icon: "fa-pen text-secondary",
      onClick: openEmailModal,
      tooltip: translate.Edit
    }),
    deleteActivityAction(
      data.MessageID,
      translate.ScheduledEmailDeleteTitle,
      translate.ScheduledEmailDeleteMessage,
      reload,
      () => ToastService.showToast(translate.ErrorProcessingRequest)
    )
  ];

  const iconNode = (
    <img className="img-fluid" src={iconEmail} alt="icon email" />
  );

  const headerNode = (
    <div className="type">
      {translate.Email}
      <span className="creator">
        {" "}
        {translate.From.toLowerCase()} {data.From}
      </span>
    </div>
  );

  const contentNode = (
    <div>
      {data.Subject && (
        <small>
          <em>
            <strong>
              {translate.Subject}: {data.Subject}
            </strong>
          </em>
        </small>
      )}
      <SafeHtml html={data.Content} style={{ overflow: "auto" }} />
      <TagIcon tags={data.tagList} />
    </div>
  );

  const dateNode = (
    <div className="dateTertiaryQ fw-bold">
      {data.Draft
        ? translate.Draft
        : formatDateDigits(new Date(data.ScheduledDate))}
    </div>
  );

  return (
    <ActivityItemShell
      cardClassName="historyCard"
      iconNode={iconNode}
      headerNode={headerNode}
      dateNode={dateNode}
      content={contentNode}
      actions={CompanyService.canDo("newmail") ? actions : []}
      respectViewContext={false}
    />
  );
};

const getScheduledTime = (data: ActivityPendingResponse.Scheduledtask) => {
  const { translate } = TranslationService;
  const days = [
    translate.Sunday,
    translate.Monday,
    translate.Tuesday,
    translate.Wednesday,
    translate.Thursday,
    translate.Friday,
    translate.Saturday
  ];
  switch (data.RepeatPeriod) {
    case 0:
      return translate.Daily;
    case 1:
      return `${translate.Weekly} ${translate.On.toLowerCase()} ${days[data.RepeatFrequency === 0 ? 1 : data.RepeatFrequency - 1]?.toLowerCase()}`;
    case 2:
      return `${translate.Monthly} ${translate.On.toLowerCase()} ${data.RepeatFrequency}`;
    default:
      return "";
  }
};

const ScheduledTaskItem = ({
  data,
  reload,
  person
}: {
  data: ActivityPendingResponse.Scheduledtask;
  reload: () => void;
  person?: Client;
}) => {
  const { translate } = TranslationService;

  const openEditModal = () =>
    FloatingPanelService.showPanel({
      children: (
        <ScheduledTaskTableProvider userId={undefined}>
          <NewScheduledTaskForm
            scheduleTask={data}
            onNewTask={reload}
            personId={person?.PersonId?.toString()}
            personName={person?.Name ?? ""}
          />
        </ScheduledTaskTableProvider>
      ),
      title: "",
      height: 800,
      width: 750
    });

  const creator =
    CompanyService.getUsers()?.find(
      (x) => x.Id === data.ResponsibleUserID?.toString()
    )?.Value ?? translate.NotAssigned;

  const actions: ActivityShellAction[] = [
    ...addIf(CompanyService.canDo("edittask"), {
      icon: "fa-pen text-secondary",
      onClick: openEditModal,
      tooltip: translate.Edit
    }),
    ...addIf(
      CompanyService.canDo("deletetask"),
      deleteScheduledTaskAction(data.ScheduledTaskID, reload, () =>
        ToastService.showToast(translate.ErrorProcessingRequest)
      )
    )
  ];

  const iconNode = <img className="img-fluid" src={iconTask} alt="icon task" />;

  const headerNode = (
    <div className="type">
      {translate.Task}
      <span className="creator">
        {" "}
        {translate.To.toLowerCase()} {creator}
      </span>
    </div>
  );

  const contentNode = (
    <div style={{ whiteSpace: "pre-line" }}>{data.Description}</div>
  );

  const dateNode = (
    <div className="dateTertiaryQ fw-bold">{getScheduledTime(data)}</div>
  );

  return (
    <ActivityItemShell
      cardClassName="historyCard"
      iconNode={iconNode}
      headerNode={headerNode}
      dateNode={dateNode}
      content={contentNode}
      actions={
        CompanyService.canDoAny(["edittask", "deletetask"]) ? actions : []
      }
      respectViewContext={false}
    />
  );
};

export interface ActivityPendingSectionProps {
  personid: string;
  groupid?: number;
  selected: number;
  person?: Client;
  setReloadPending?: (fn: () => void) => void;
  onPendingChange?: () => void;
}

export const ActivityPendingSection = ({
  personid,
  groupid,
  selected,
  person,
  setReloadPending,
  onPendingChange
}: ActivityPendingSectionProps) => {
  const { translate } = TranslationService;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [response, setResponse] = useState<ActivityPendingResponse>();
  const hasResponse = useRef(false);

  const reload = useCallback(
    (action?: (val?: unknown) => void) => {
      const getData = async () => {
        const result = await ActivityService.getPending(personid, groupid);
        if (result instanceof Error) {
          setError(true);
        } else {
          setError(false);
          setResponse(result);
          hasResponse.current = true;
        }
        setLoading(false);
        if (action && typeof action === "function") {
          action(false);
        }
      };
      if (!hasResponse.current) {
        setLoading(true);
      }
      getData();
    },
    [personid, groupid]
  );

  const reloadAll = useCallback(
    (action?: (val?: unknown) => void) => {
      reload(action);
      onPendingChange?.();
    },
    [reload, onPendingChange]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  if (setReloadPending) {
    setReloadPending(() => reload());
  }

  if (loading) return <Loading />;
  if (error) {
    return (
      <ErrorMessage
        onRefresh={reload}
        message={translate.ErrorProcessingRequest}
      />
    );
  }
  if (!response) return null;

  const hasTasks = response.tasks?.length > 0;
  const hasMails = response.mails?.length > 0;
  const hasScheduled = response.scheduledtasks?.length > 0;

  if (!hasTasks && !hasMails && !hasScheduled) return null;

  const showTasks = selected === 0 || selected === 3;
  const showMails = selected === 0 || selected === 1;

  return (
    <div className="listResults">
      {showTasks &&
        hasTasks &&
        response.tasks.map((task) => (
          <PendingTaskItem key={task.TaskID} data={task} reload={reloadAll} />
        ))}
      {showMails &&
        hasMails &&
        response.mails.map((mail) => (
          <ScheduledEmailItem
            key={mail.MessageID}
            data={mail}
            reload={reloadAll}
          />
        ))}
      {showTasks &&
        hasScheduled &&
        response.scheduledtasks.map((st) => (
          <ScheduledTaskItem
            key={st.ScheduledTaskID}
            data={st}
            reload={reloadAll}
            person={person}
          />
        ))}
    </div>
  );
};
